import { mutatePurchaseOrderItems } from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormSelect } from "@/features/standard/Form/FormSelect";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import customAlert from "@/features/ui/alert/alert";
import { ItemAutoComplete } from "@/features/ui/autoComplete/item/item-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { useUserStore } from "@/store/userStore";
import { getUserCodeByUser } from "@/utils/checkData";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { validateFormValues } from "@/utils/validation";
import { Button, Flex, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  PartnersGet200ResponseRowsInnerTypeEnum,
  ScmApiPurchaseOrderItemsPurchaseOrderItemIdPutRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const Update = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  Text: Text,
  Flex: Flex,
  DateInput: DateInput,
});

interface PurchaseOrderUpdateFormProps {
  purchaseOrderId?: number;
  formatterProps: AuthSignupPost201ResponseEquipmentPurchaseOrderItem;
}

export const PurchaseOrderItemsUpdateForm = (props: PurchaseOrderUpdateFormProps) => {
  const { purchaseOrderId, formatterProps } = props;
  const userCode = getUserCodeByUser();
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { userInfo } = useUserStore();
  const partnerType = userInfo?.partner?.type;

  const { mutate: createMutate } = useMutation(
    (params: ScmApiPurchaseOrderItemsPurchaseOrderItemIdPutRequest) =>
      mutatePurchaseOrderItems
        .update(params)
        .mutationFn(params as ScmApiPurchaseOrderItemsPurchaseOrderItemIdPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["purchaseOrderItems"]);
        closeModal(form.values);
        customAlert(
          t("message.editSuccess", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          }),
          t("common.editSuccess", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          }),
          "green"
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: translateWithPartnerType(
              t,
              "purchaseOrderItems.purchaseOrderItems",
              partnerType
            ),
          }),
          t("common.editFail", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          }),
          "red"
        );
      },
    }
  );

  const form = useForm({
    initialValues: {
      partnerCode: formatterProps.partnerCode,
      itemCode: formatterProps.itemCode,
      quantity: formatterProps.quantity,
      orderDate: formatterProps.orderDate ? new Date(formatterProps.orderDate) : undefined,
      deliveryDeadline: formatterProps.deliveryDeadline
        ? new Date(formatterProps.deliveryDeadline)
        : undefined,
      sourceLocationCode: formatterProps.sourceLocationCode,
    },
  });

  const onSubmit = async () => {
    try {
      createMutate({
        purchaseOrderItemId: formatterProps.id ?? 0,
        purchaseOrderItemsGetRequest: {
          purchaseOrderId: formatterProps.purchaseOrderId,
          partnerCode: form.values.partnerCode!,
          direction: "INBOUND",
          itemCode: form.values.itemCode!,
          quantity: form.values.quantity!,
          orderDate: form.values.orderDate?.toDateString()!,
          deliveryDeadline: form.values.deliveryDeadline!?.toDateString(),
          sourceLocationCode: form.values.sourceLocationCode!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({
          error,
          errorContext: translateWithPartnerType(
            t,
            "purchaseOrderItems.purchaseOrderItems",
            partnerType
          ),
        }),
        t("common.editFail", {
          item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
        }),
        "red"
      );
    }
  };

  return (
    <>
      <Update.Wrapper>
        {/* <PurchaseOrderAutoComplete
          label={translateWithPartnerType(t, "purchaseOrder.code", partnerType)}
          placeholder={t("placeholder.select", {
            item: translateWithPartnerType(t, "purchaseOrder.code", partnerType),
          })}
          {...form.getInputProps("purchaseOrderId")}
          defaultValue={formatterProps.purchaseOrderCode}
          error={form.isDirty("purchaseOrderId") ? t("common.toSave") : ""}
          direction="INBOUND"
        /> */}
        <PartnerAutoComplete
          label={t("partner.partner")}
          placeholder={t("placeholder.select", { item: t("partner.partner") })}
          defaultValue={formatterProps.partnerCode}
          {...form.getInputProps("partnerCode")}
          partnerType={[
            partnerType ?? PartnersGet200ResponseRowsInnerTypeEnum.SALES,
            PartnersGet200ResponseRowsInnerTypeEnum.BI_DIRECTION,
          ]}
          withAsterisk
          error={form.isDirty("partnerCode") ? t("common.toSave") : ""}
        />
        <ItemAutoComplete
          label={t("item.code")}
          placeholder={t("placeholder.select", { item: t("item.code") })}
          {...form.getInputProps("itemCode")}
          getItemType={["PRODUCT", "SEMI_PRODUCT"]}
          defaultValue={formatterProps.itemCode}
          error={form.isDirty("itemCode") ? t("common.toSave") : ""}
        />
        <Update.Input
          label={t("moldCavity.quantity")}
          placeholder={t("placeholder.input", { item: t("moldCavity.quantity") })}
          {...form.getInputProps("quantity")}
          defaultValue={formatterProps.quantity}
          error={form.isDirty("quantity") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("quantity") ? "red !important" : "",
              borderColor: form.isDirty("quantity") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <LocationAutoComplete
          label={translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType)}
          placeholder={t("placeholder.select", {
            item: translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType),
          })}
          {...form.getInputProps("sourceLocationCode")}
          defaultValue={formatterProps.sourceLocationCode}
          error={form.isDirty("sourceLocationCode") ? t("common.toSave") : ""}
        />
        <Update.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={translateWithPartnerType(t, "purchaseOrder.orderDate", partnerType)}
          valueFormat="YYYY-MM-DD"
          locale="ko"
          firstDayOfWeek={0}
          {...form.getInputProps("orderDate")}
          error={form.isDirty("orderDate") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("orderDate") ? "red !important" : "",
              borderColor: form.isDirty("orderDate") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={t("order.deliveryDate")}
          valueFormat="YYYY-MM-DD"
          locale="ko"
          firstDayOfWeek={0}
          minDate={form.values.orderDate ? new Date(form.values.orderDate) : undefined}
          {...form.getInputProps("deliveryDeadline")}
          error={form.isDirty("deliveryDeadline") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("deliveryDeadline") ? "red !important" : "",
              borderColor: form.isDirty("deliveryDeadline") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.ButtonBox>
          <Update.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </Update.Button>
          <Update.Button
            disabled={validateFormValues({
              pageType: "purchaseOrderItems",
              values: form.values,
            })}
            onClick={onSubmit}
          >
            {t("common.edit", {
              item: translateWithPartnerType(
                t,
                "purchaseOrderItems.purchaseOrderItems",
                partnerType
              ),
            })}
          </Update.Button>
        </Update.ButtonBox>
      </Update.Wrapper>
    </>
  );
};
